class SizeChart extends HTMLElement {
  constructor() {
    super();

    this.chartData   = null;
    this.bodyImages  = [];
    this.currentUnit = 'cm';
    this.currentType = 'flat';
    this.wearType    = 'topwear';

    this.unitLabels = { cm: '(CM)', in: '(Inches)' };
    this.letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

    this.sliderState = { height: 170, chest: 100, waist: 80 };

    this.bodyTypes = [
      { id: 'P1', height: 158, chest: 84, waist: 68 },
      { id: 'P2', height: 165, chest: 90, waist: 74 },
      { id: 'P3', height: 168, chest: 96, waist: 82 },
      { id: 'P4', height: 172, chest: 88, waist: 72 },
      { id: 'P5', height: 175, chest: 102, waist: 88 },
      { id: 'P6', height: 178, chest: 108, waist: 94 },
      { id: 'P7', height: 180, chest: 112, waist: 100 },
      { id: 'P8', height: 170, chest: 118, waist: 108 },
      { id: 'P9', height: 168, chest: 110, waist: 100 },
      { id: 'P10', height: 195, chest: 120, waist: 104 },
    ];
  }

  connectedCallback() {
    if (!this.dataset.chart) return;

    this.chartData = JSON.parse(this.dataset.chart);
    this.wearType  = this.dataset.wearType || 'topwear';

    try {
      const raw = JSON.parse(this.dataset.bodyImages || '[]');
      this.bodyImages = raw.map(img => {
        const { personId, size } = this.parseBodyImageAlt(img.alt || img.url);
        return { ...img, personId, size };
      });
    } catch {
      this.bodyImages = [];
    }

    if (!this.bodyImages.length) {
      this.hideBodyFitFeatures();
    }

    this.renderHeader();
    this.renderTable();
    this.bindToggleEvents();
    this.bindSliderEvents();
    this.bindNumberInputs();
    this.updateNumberInputs();

    if (this.isWithinRange()) {
      this.updateBodyFit();
    }

    setTimeout(() => this.updateAllSlidersUI(), 300);
  }

  roundSmart(num) {
    const rounded = Math.round(num * 10) / 10;
    return rounded % 1 === 0 ? Math.round(rounded) : rounded;
  }

  cmToIn(cm) {
    return Math.round((cm / 2.54) * 10) / 10;
  }

  cmToFeetInches(cm) {
    const totalIn = Math.round(cm / 2.54);
    const feet = Math.floor(totalIn / 12);
    const inches = totalIn % 12;
    return `${feet}'${inches}"`;
  }

  formatMeasurement(cm) {
    return this.currentUnit === 'in'
      ? `${this.cmToIn(cm).toFixed(1)}in`
      : `${cm}cm`;
  }

  parseBodyImageAlt(alt = '') {
    const base = alt.split('/').pop().replace(/\.[^.]+$/, '');
    const [personId, size] = base.split('_')[0].split('-');
    return { personId: personId?.toUpperCase(), size: size?.toUpperCase() };
  }

  findClosestBodyType(userHeight, userChest, userWaist) {
    const isBottom = this.wearType === 'bottomwear';

    const score = (bt) => {
      const dHeight = Math.abs(bt.height - userHeight);
      const dChest  = Math.abs(bt.chest  - userChest);
      const dWaist  = Math.abs(bt.waist  - userWaist);

      if (isBottom) {
        return dWaist * 100 + dHeight * 25 + dChest * 8;
      } else {
        return dHeight * 100 + dChest * 25 + dWaist * 8;
      }
    };

    let best = null;
    let bestScore = Infinity;

    for (let i = 0; i < this.bodyTypes.length; i++) {
      const bt = this.bodyTypes[i];
      const currentScore = score(bt);

      if (currentScore < bestScore) {
        best = bt;
        bestScore = currentScore;
      }
    }

    return { best, bestScore };
  }

  isWithinRange() {
    const { height, chest, waist } = this.sliderState;

    const getRange = arr => [Math.min(...arr) - 10, Math.max(...arr) + 10];

    const [hMin, hMax] = getRange(this.bodyTypes.map(b => b.height));
    const [cMin, cMax] = getRange(this.bodyTypes.map(b => b.chest));
    const [wMin, wMax] = getRange(this.bodyTypes.map(b => b.waist));

    return (
      height >= hMin && height <= hMax &&
      chest  >= cMin && chest  <= cMax &&
      waist  >= wMin && waist  <= wMax
    );
  }

  renderHeader() {
    const thead = this.querySelector('#sizechart-thead');
    if (!thead) return;

    let html = `<tr><th>${this.unitLabels[this.currentUnit]}</th>`;
    this.chartData.sizes.forEach(size => html += `<th>${size}</th>`);
    html += '</tr>';

    thead.innerHTML = html;
  }

  renderTable() {
    const tbody = this.querySelector('#sizechart-tbody');
    if (!tbody) return;

    let html = '';

    this.chartData.measurements.forEach((m, i) => {
      const letter = this.letters[i] || (i + 1);

      html += `<tr>
        <td class="measurement-label">
          <span class="letter-label">${letter}.</span>
          ${m.label}
        </td>`;

      m.values.forEach(val => {
        const cm = parseFloat(val);
        const inch = this.roundSmart(cm / 2.54);

        html += `<td class="measurement-value" data-cm="${cm}" data-in="${inch}">
          <span class="value">${this.roundSmart(cm)}</span>
        </td>`;
      });

      html += `</tr>`;
    });

    tbody.innerHTML = html;
  }

  updateBodyFit() {
    const { height, chest, waist } = this.sliderState;
    const { best, bestScore } = this.findClosestBodyType(height, chest, waist);

    if (bestScore > 1000) {
      this.showNoMatch();
      return;
    }

    const img = this.bodyImages.find(i => i.personId === best.id);

    const imgEl = this.querySelector('#body-fit-img');
    const heroText = this.querySelector('.results-recommendation-text');

    // Desktop block (.results-container) and mobile overlay copy
    // (.model-image-wrap) hold the same lines; CSS shows one per breakpoint,
    // so both must be populated.
    const measBlocks = Array.from(
      this.querySelectorAll('#model-measurements, #model-measurements-mobile')
    );

    if (!imgEl || !measBlocks.length) return;

    imgEl.src = img?.url || '';

    heroText.textContent = `Closest Model Match: Size ${img?.size || best.id}`;

    this.querySelectorAll('.model-fit').forEach(el => {
      el.innerHTML = `Model wears <strong>${img?.size || best.id}</strong>`;
    });

    const formatDiff = (diffCm, label) => {
      if (diffCm === 0) {
        return `Model ${label} is Exactly Like Yours`;
      }

      const isIn = this.currentUnit === 'in';

      let val = isIn ? this.cmToIn(Math.abs(diffCm)) : Math.abs(diffCm);
      val = Math.round(val * 10) / 10;

      const unit = isIn ? 'in' : 'cm';

      if (label === 'Height') {
        return `Model is <strong>${val}${unit} ${
          diffCm > 0 ? 'Taller' : 'Shorter'
        }</strong> Than You`;
      }

      return `Model ${label} is <strong>${val}${unit} ${
        diffCm > 0 ? 'Larger' : 'Smaller'
      }</strong> Than Yours`;
    };

    const map = {
      height: {
        selector: '.height-measurement',
        model: best.height,
        user: height,
        label: 'Height'
      },
      chest: {
        selector: '.chest-measurement',
        model: best.chest,
        user: chest,
        label: 'Chest'
      },
      waist: {
        selector: '.waist-measurement',
        model: best.waist,
        user: waist,
        label: 'Waist'
      }
    };

    const order =
      this.wearType === 'bottomwear'
        ? ['height', 'waist', 'chest']
        : ['height', 'chest', 'waist'];

    order.forEach((key) => {
      const item = map[key];

      const diff = item.model - item.user;
      const html = formatDiff(diff, item.label);

      measBlocks.forEach(block => {
        const el = block.querySelector(item.selector);

        if (!el) return;

        el.innerHTML = html;

        el.classList.remove('text-green', 'text-red');

        if (diff > 0) {
          el.classList.add('text-green');
        } else if (diff < 0) {
          el.classList.add('text-red');
        }
      });
    });
  }

  showNoMatch() {
    const container = this.querySelector('#body-size-container');

    container.classList.remove('loading');
    container.classList.add('result');
    container.classList.add('no-result');
  }

  updateSliderUI(slider) {
    const wrapper = slider.closest('.slider-track-wrap');
    const valueEl = wrapper?.querySelector('.slider-value');
    if (!valueEl) return;

    const val = +slider.value;

    if (slider.id === 'slider-height') {
      valueEl.textContent = this.currentUnit === 'in'
        ? this.cmToFeetInches(val)
        : `${val}cm`;
    } else {
      valueEl.textContent = this.currentUnit === 'in'
        ? `${Math.round(this.cmToIn(val))}in`
        : `${val}cm`;
    }

    const percent = (val - slider.min) / (slider.max - slider.min);
    const width = slider.getBoundingClientRect().width;

    if (!width) return;

    const thumbWidth = 40;
    const offset = percent * (width - thumbWidth) + thumbWidth / 2;

    valueEl.style.left = `${offset}px`;
  }

  updateAllSlidersUI() {
    this.querySelectorAll('.body-slider').forEach(slider => {
      this.updateSliderUI(slider);
    });
  }

  // Canonical state is cm. Convert a cm value into the slider's range,
  // clamp it, sync the slider + state + all UI (bubble and number inputs).
  setFieldValue(field, cm) {
    const slider = this.querySelector(`#slider-${field}`);
    if (!slider) return;

    const min = +slider.min;
    const max = +slider.max;

    let v = Math.round(cm);
    if (isNaN(v)) v = +slider.value;
    v = Math.max(min, Math.min(max, v));

    slider.value = v;
    this.sliderState[field] = v;

    this.updateSliderUI(slider);
    this.updateNumberInputs();
  }

  // Reflect the current slider positions into the number inputs, in the
  // active unit. Height shows a single cm input or split ft/in inputs.
  updateNumberInputs() {
    const isIn = this.currentUnit === 'in';

    ['chest', 'waist'].forEach(field => {
      const input = this.querySelector(`#input-${field}`);
      if (!input) return;

      const cm = this.sliderState[field];
      input.value = isIn ? Math.round(this.cmToIn(cm)) : cm;

      const label = input.closest('.slider-input-wrap')
        ?.querySelector('.slider-input-unit');
      if (label) label.textContent = isIn ? 'in' : 'cm';
    });

    const hcm = this.sliderState.height;
    const cmInput = this.querySelector('#input-height-cm');
    const ftInput = this.querySelector('#input-height-ft');
    const inInput = this.querySelector('#input-height-in');
    const cmWrap = this.querySelector('.height-cm-input');
    const inWrap = this.querySelector('.height-in-input');

    if (cmInput) cmInput.value = hcm;

    if (ftInput && inInput) {
      const totalIn = Math.round(hcm / 2.54);
      ftInput.value = Math.floor(totalIn / 12);
      inInput.value = totalIn % 12;
    }

    // Use explicit 'flex' (not '') so it overrides the stylesheet's
    // default `display: none` on the imperial wrapper.
    if (cmWrap) cmWrap.style.display = isIn ? 'none' : 'flex';
    if (inWrap) inWrap.style.display = isIn ? 'flex' : 'none';
  }

  bindNumberInputs() {
    ['chest', 'waist'].forEach(field => {
      const input = this.querySelector(`#input-${field}`);
      if (!input) return;

      input.addEventListener('change', () => {
        const raw = parseFloat(input.value);
        if (isNaN(raw)) { this.updateNumberInputs(); return; }

        const cm = this.currentUnit === 'in' ? raw * 2.54 : raw;
        this.setFieldValue(field, cm);
      });
    });

    const cmInput = this.querySelector('#input-height-cm');
    if (cmInput) {
      cmInput.addEventListener('change', () => {
        const raw = parseFloat(cmInput.value);
        if (isNaN(raw)) { this.updateNumberInputs(); return; }
        this.setFieldValue('height', raw);
      });
    }

    const ftInput = this.querySelector('#input-height-ft');
    const inInput = this.querySelector('#input-height-in');
    if (ftInput && inInput) {
      const onHeightImperial = () => {
        const ft = parseFloat(ftInput.value);
        const inch = parseFloat(inInput.value);
        if (isNaN(ft) && isNaN(inch)) { this.updateNumberInputs(); return; }

        const totalIn = (isNaN(ft) ? 0 : ft) * 12 + (isNaN(inch) ? 0 : inch);
        this.setFieldValue('height', totalIn * 2.54);
      };

      ftInput.addEventListener('change', onHeightImperial);
      inInput.addEventListener('change', onHeightImperial);
    }
  }

  bindToggleEvents() {
    this.querySelectorAll('.unit-btn').forEach(btn => {
      btn.onclick = () => this.switchUnit(btn.dataset.unit);
    });

    this.querySelectorAll('.type-btn').forEach(btn => {
      btn.onclick = () => this.switchType(btn.dataset.type);
    });
  }

  bindSliderEvents() {
    ['height','chest','waist'].forEach(field => {
      const slider = this.querySelector(`#slider-${field}`);
      if (!slider) return;

      // Slider values are always in cm; seed state so matching reflects
      // the visible slider positions even before the user drags anything.
      this.sliderState[field] = +slider.value;

      slider.addEventListener('input', () => {
        this.sliderState[field] = +slider.value;
        this.updateSliderUI(slider);
        this.updateNumberInputs();
      });
    });

    const btn = this.querySelector('#find-my-fit-btn');
    if (btn) {
      btn.onclick = () => {
        const container = this.querySelector('#body-size-container');

        container.classList.add('loading');
        container.classList.remove('result');

        setTimeout(() => {
          if (!this.isWithinRange()) {
            this.showNoMatch();
            return;
          }

          this.updateBodyFit();

          container.classList.remove('loading');
          container.classList.add('result');
        }, 300);
      };
    }

    const TryAgainBtn = this.querySelector('#try-again-btn');
    if (TryAgainBtn) {
      TryAgainBtn.onclick = () => {
        const container = this.querySelector('#body-size-container');

        container.classList.remove('result', 'no-result');
      };
    }

    const finishBtn = this.querySelector('#finish-btn');

    if (finishBtn) {
      finishBtn.onclick = () => {
        const targetId = finishBtn.dataset.gotoId;
        const targetEl = document.getElementById(targetId);

        if (!targetEl) return;

        targetEl.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      };
    }
  }

  switchUnit(unit) {
    if (this.currentUnit === unit) return;

    this.currentUnit = unit;

    this.querySelectorAll('.unit-btn').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.unit === unit);
    });

    const th = this.querySelector('#sizechart-thead th:first-child');
    if (th) th.textContent = this.unitLabels[unit];

    this.querySelectorAll('.measurement-value').forEach(cell => {
      const val = unit === 'cm'
        ? parseFloat(cell.dataset.cm)
        : parseFloat(cell.dataset.in);

      cell.querySelector('.value').textContent = this.roundSmart(val);
    });

    this.updateAllSlidersUI();
    this.updateNumberInputs();

    // Only re-render the fit result if one is already on screen. Switching
    // units from the slider view must just update metrics, not trigger a
    // (no-)match result.
    const container = this.querySelector('#body-size-container');
    if (container?.classList.contains('result')) {
      this.updateBodyFit();
    }
  }

  switchType(type) {
    this.currentType = type;

    this.querySelectorAll('.type-btn').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.type === type);
    });

    this.querySelectorAll('[data-content-type]').forEach(el => {
      el.classList.toggle('active', el.dataset.contentType === type);
    });

    this.updateAllSlidersUI();
  }

  hideBodyFitFeatures() {
    this.querySelector('.type-selector')?.remove();
    this.querySelectorAll('[data-content-type="body"]').forEach(el => el.remove());
  }
}

if (!customElements.get('sizechart-wrapper')) {
  customElements.define('sizechart-wrapper', SizeChart);
}